import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, query, where, doc, updateDoc,
  serverTimestamp, onSnapshot
} from '../../firebase/config';
import {
  Package, User, MapPin, Clock, Truck, Check, X,
  Store, CreditCard, ChevronDown, Lock, Mail,
  Copy, Info
} from 'lucide-react';

// ---------- Courier definitions ----------
const COURIER_INFO = {
  collect: {
    name: 'Collect from vendor',
    icon: Store,
    color: 'green',
    type: 'pickup',
  },
  pep_paxi: {
    name: 'Pep Paxi',
    icon: Package,
    color: 'blue',
    type: 'store',
  },
  pudo: {
    name: 'Pudo Locker',
    icon: Lock,
    color: 'purple',
    type: 'locker',
  },
  courier_guy: {
    name: 'The Courier Guy',
    icon: Truck,
    color: 'pink',
    type: 'door',
  },
  postnet: {
    name: 'PostNet',
    icon: Mail,
    color: 'amber',
    type: 'counter',
  },
};

const ProfessionalOrdersPage = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const tabs = [
    { id: 'pending', label: 'New' },
    { id: 'preparing', label: 'Preparing' },
    { id: 'shipped', label: 'In Transit' },
    { id: 'ready_for_collection', label: 'Ready' },
    { id: 'delivered', label: 'Delivered' },
    { id: 'collected', label: 'Collected' },
  ];

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'orders'),
      where('vendorId', '==', user.uid)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return bT - aT;
      });
      setOrders(data);
      setLoading(false);
    }, (err) => {
      console.error('Orders snapshot error:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  useEffect(() => {
    setFilteredOrders(orders.filter(o => o.fulfillmentStatus === activeTab));
  }, [activeTab, orders]);

  const getTabCount = (status) => orders.filter(o => o.fulfillmentStatus === status).length;

  // ---------- Status transitions ----------
  const updateFulfillment = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        fulfillmentStatus: newStatus,
        updatedAt: serverTimestamp(),
        [`statusHistory.${newStatus}`]: serverTimestamp(),
      });
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to update order');
    }
  };

  // ---------- Helpers ----------
  const formatPrice = (n) => `R${Number(n || 0).toFixed(2)}`;

  const formatDate = (ts) => {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending': return { bg: 'bg-amber-50', text: 'text-amber-600', label: 'New' };
      case 'preparing': return { bg: 'bg-blue-50', text: 'text-blue-600', label: 'Preparing' };
      case 'shipped': return { bg: 'bg-purple-50', text: 'text-purple-600', label: 'In Transit' };
      case 'ready_for_collection': return { bg: 'bg-green-50', text: 'text-green-600', label: 'Ready for pickup' };
      case 'delivered': return { bg: 'bg-green-50', text: 'text-green-600', label: 'Delivered' };
      case 'collected': return { bg: 'bg-green-50', text: 'text-green-600', label: 'Collected' };
      case 'cancelled': return { bg: 'bg-red-50', text: 'text-red-500', label: 'Cancelled' };
      default: return { bg: 'bg-gray-50', text: 'text-gray-500', label: status };
    }
  };

  const getCourierInfo = (order) => {
    return COURIER_INFO[order.deliveryMethod] || {
      name: order.deliveryProvider || 'Delivery',
      icon: Truck,
      color: 'pink',
      type: 'door',
    };
  };

  const getCourierColorClasses = (color) => {
    const map = {
      green: { bg: 'bg-green-50', text: 'text-green-600', ring: 'ring-green-100' },
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-100' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', ring: 'ring-purple-100' },
      pink: { bg: 'bg-[#FFF5F6]', text: 'text-[#EFA9B6]', ring: 'ring-pink-100' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-100' },
    };
    return map[color] || map.pink;
  };

  const isPickup = (order) => order.deliveryMethod === 'collect';
  const isShipping = (order) =>
    order.deliveryMethod === 'pep_paxi' ||
    order.deliveryMethod === 'pudo' ||
    order.deliveryMethod === 'courier_guy' ||
    order.deliveryMethod === 'postnet' ||
    order.deliveryMethod === 'standard' ||
    order.deliveryMethod === 'express';

  const handleCopyOrderId = async (id) => {
    try {
      await navigator.clipboard.writeText(`#${id.slice(-6).toUpperCase()}`);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto pb-20">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Orders</h1>
        <p className="text-gray-500 mt-1">Manage product orders from your clients</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`bg-white rounded-2xl p-3 border cursor-pointer transition-all ${
              activeTab === tab.id ? 'border-[#CDBCE7]' : 'border-gray-100 hover:border-[#CDBCE7]'
            }`}
          >
            <p className="text-xl font-bold text-gray-900">{getTabCount(tab.id)}</p>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5 truncate">
              {tab.label}
            </p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-6 pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-colors border ${
              activeTab === tab.id
                ? 'bg-[#010101] text-white border-[#010101]'
                : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7]'
            }`}
          >
            {tab.label}
            {getTabCount(tab.id) > 0 && (
              <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] ${
                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-[#F4EEFF] text-[#AF8BD4]'
              }`}>
                {getTabCount(tab.id)}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Orders */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
          <div className="w-20 h-20 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-4">
            <Package size={32} className="text-[#AF8BD4]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
            No {activeTab.replace('_', ' ')} orders
          </h3>
          <p className="text-gray-400 text-sm">
            {activeTab === 'pending'
              ? 'New orders will appear here when clients purchase your products'
              : `Your ${activeTab.replace('_', ' ')} orders will show here`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const badge = getStatusBadge(order.fulfillmentStatus);
            const isExpanded = expandedOrder === order.id;
            const courier = getCourierInfo(order);
            const courierColors = getCourierColorClasses(courier.color);
            const CourierIcon = courier.icon;

            return (
              <div
                key={order.id}
                className="bg-white rounded-3xl border border-gray-100 hover:border-[#CDBCE7] transition-colors overflow-hidden"
              >
                {/* Summary row */}
                <button
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                  className="w-full p-4 flex items-center gap-3 text-left"
                >
                  {/* Courier icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${courierColors.bg} ${courierColors.text}`}>
                    <CourierIcon size={20} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
                        {badge.label}
                      </span>
                      <span className="text-[10px] text-gray-400 font-mono">
                        #{order.id.slice(-6).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {order.clientName || 'Client'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {courier.name} · {order.items?.length || 0} item{order.items?.length === 1 ? '' : 's'} · {formatPrice(order.total)}
                    </p>
                  </div>

                  <ChevronDown
                    size={18}
                    className={`text-gray-400 transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="border-t border-gray-50 p-4 space-y-4">

                    {/* Courier highlight */}
                    <div className={`rounded-2xl p-4 ${courierColors.bg}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-white/60 flex items-center justify-center shrink-0 ${courierColors.text}`}>
                          <CourierIcon size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-[10px] font-bold uppercase tracking-wider ${courierColors.text}`}>
                            Delivery Method
                          </p>
                          <p className="font-bold text-gray-900 text-sm mt-0.5">
                            {courier.name}
                          </p>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {order.deliveryEstimate || courier.type}
                          </p>

                          {/* Collect address */}
                          {isPickup(order) && order.collectAddress && (
                            <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-700">
                              <MapPin size={12} className="mt-0.5 shrink-0" />
                              <span>{order.collectAddress}</span>
                            </div>
                          )}

                          {/* Door delivery address */}
                          {!isPickup(order) && order.deliveryAddress && (
                            <div className="mt-2 bg-white/60 rounded-xl p-3 text-xs text-gray-700 space-y-0.5">
                              <p className="font-bold text-gray-900 mb-1">{order.deliveryAddress.fullName}</p>
                              <p>{order.deliveryAddress.line1}</p>
                              <p>
                                {order.deliveryAddress.city}
                                {order.deliveryAddress.postalCode ? `, ${order.deliveryAddress.postalCode}` : ''}
                              </p>
                              <p>{order.deliveryAddress.country}</p>
                              {order.clientPhone && (
                                <p className="text-gray-500 pt-1">{order.clientPhone}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Client */}
                    <div className="bg-[#FCF8F9] rounded-2xl p-3">
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Client
                      </p>
                      <p className="text-sm font-bold text-gray-900">{order.clientName}</p>
                      {order.clientEmail && <p className="text-xs text-gray-500">{order.clientEmail}</p>}
                      {order.clientPhone && <p className="text-xs text-gray-500">{order.clientPhone}</p>}
                    </div>

                    {/* Items */}
                    <div>
                      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                        Items ({order.items?.length || 0})
                      </p>
                      <div className="space-y-2">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#FCF8F9] flex items-center justify-center overflow-hidden shrink-0">
                              {item.image ? (
                                <img src={item.image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <Package size={16} className="text-gray-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                              <p className="text-xs text-gray-400">× {item.quantity}</p>
                            </div>
                            <span className="font-bold text-sm text-gray-900">
                              {formatPrice((item.price || 0) * item.quantity)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Payment summary */}
                    <div className="bg-[#FCF8F9] rounded-2xl p-3 space-y-1.5 text-sm">
                      <div className="flex justify-between text-gray-500">
                        <span>Subtotal</span>
                        <span>{formatPrice(order.subtotal)}</span>
                      </div>
                      <div className="flex justify-between text-gray-500">
                        <span>Delivery ({courier.name})</span>
                        <span>{order.deliveryFee === 0 ? 'Free' : formatPrice(order.deliveryFee)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-gray-900 pt-1.5 border-t border-gray-100">
                        <span>Total paid</span>
                        <span>{formatPrice(order.total)}</span>
                      </div>
                      <p className="text-[10px] text-gray-400 pt-1">
                        Paid on {formatDate(order.createdAt)}
                      </p>
                    </div>

                    {/* Order ID with copy */}
                    <div className="flex items-center justify-between text-xs text-gray-500 bg-[#FCF8F9] rounded-xl px-3 py-2">
                      <span className="font-mono">Order #{order.id.slice(-8).toUpperCase()}</span>
                      <button
                        type="button"
                        onClick={() => handleCopyOrderId(order.id)}
                        className={`flex items-center gap-1 font-bold transition-colors ${
                          copiedId === order.id ? 'text-green-600' : 'text-[#AF8BD4] hover:underline'
                        }`}
                      >
                        <Copy size={11} />
                        {copiedId === order.id ? 'Copied' : 'Copy'}
                      </button>
                    </div>

                    {/* Actions — vary by fulfillment status AND delivery type */}
                    <div className="flex flex-wrap gap-2 pt-2">

                      {/* PENDING */}
                      {order.fulfillmentStatus === 'pending' && (
                        <button
                          onClick={() => updateFulfillment(order.id, 'preparing')}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Check size={16} /> Start Preparing
                        </button>
                      )}

                      {/* PREPARING — Pickup/Collect */}
                      {order.fulfillmentStatus === 'preparing' && isPickup(order) && (
                        <button
                          onClick={() => updateFulfillment(order.id, 'ready_for_collection')}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Store size={16} /> Mark Ready for Pickup
                        </button>
                      )}

                      {/* PREPARING — Shipping */}
                      {order.fulfillmentStatus === 'preparing' && !isPickup(order) && isShipping(order) && (
                        <button
                          onClick={() => updateFulfillment(order.id, 'shipped')}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Truck size={16} /> Mark Dispatched
                        </button>
                      )}

                      {/* SHIPPED */}
                      {order.fulfillmentStatus === 'shipped' && (
                        <button
                          onClick={() => updateFulfillment(order.id, 'delivered')}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Check size={16} /> Mark Delivered
                        </button>
                      )}

                      {/* READY FOR COLLECTION */}
                      {order.fulfillmentStatus === 'ready_for_collection' && (
                        <button
                          onClick={() => updateFulfillment(order.id, 'collected')}
                          className="flex-1 sm:flex-none px-5 py-2.5 bg-[#010101] text-white text-sm font-bold rounded-full hover:bg-black/90 transition-colors flex items-center justify-center gap-2"
                        >
                          <Check size={16} /> Mark Collected
                        </button>
                      )}

                      {/* COMPLETED */}
                      {(order.fulfillmentStatus === 'delivered' || order.fulfillmentStatus === 'collected') && (
                        <p className="text-sm text-green-600 font-bold flex items-center gap-2">
                          <Check size={16} /> Order complete
                        </p>
                      )}
                    </div>

                    {/* Help note for pros */}
                    {order.fulfillmentStatus === 'preparing' && !isPickup(order) && (
                      <div className="flex items-start gap-2 p-3 bg-[#FFF5F6] rounded-xl">
                        <Info size={14} className="text-[#EFA9B6] shrink-0 mt-0.5" />
                        <p className="text-[11px] text-gray-600 leading-relaxed">
                          Drop off the parcel at your nearest <span className="font-bold text-gray-900">{courier.name}</span> point.
                          Then tap "Mark Dispatched" so the client knows it's on the way.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProfessionalOrdersPage;